import styled from '@emotion/styled';
import { useMediaQuery } from 'react-responsive';
import { ChevronIcon } from '../../../assets/icons/ChevronIcon';
import { ArrowIcon } from '../../../assets/icons/ArrowIcon';

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 0;

  @media (min-width: 768px) {
    padding: 40px 0 0 0;
    gap: 8px;
  }
`;

const PaginationNumberWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #f7f7f7;
  overflow: hidden;
  margin: 0 -1px;

  @media (min-width: 768px) {
    background: none;
    border: none;
    border-radius: 0;
    gap: 2px;
    justify-content: center;
    margin: 0;
  }
`;

const PaginationButton = styled.button<{ isActive?: boolean }>`
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 40px !important;
  height: 40px !important;
  border: none !important;
  background: ${(props) => (props.isActive ? '#F9FAFB' : '#FFFFFF')} !important;
  color: #344054 !important;
  font-family: 'Plus Jakarta Sans' !important;
  font-size: 14px !important;
  cursor: pointer !important;
  border-right: 1px solid #f7f7f7 !important;

  &:last-child {
    border-right: none;
  }

  &:disabled {
    cursor: default;
    color: #344054;
  }

  @media (min-width: 768px) {
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 9999px;
    background: ${(props) => (props.isActive ? '#F7F7F7' : '#FFFFFF')};
    color: #303030;
    border-right: none;
    padding: 10px;
    transition: all 0.2s;

    &:hover:not(:disabled) {
      background: #f7f7f7;
    }

    &:disabled {
      color: #303030;
    }
  }
`;

const ArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid #f7f7f7 !important;
  background: #ffffff;
  cursor: pointer;
  padding: 0;
  font-size: 14px !important;
  font-weight: 400 !important;

  &:disabled {
    cursor: default;
  }

  span.text {
    display: none;
  }

  @media (min-width: 768px) {
    position: absolute;
    width: auto;
    height: auto;
    padding: 8px 12px;
    gap: 6px;
    border: 1px solid #c4c4c4 !important;
    border-radius: 8px;
    background: #ffffff;
    color: #344054;
    font-family: 'Plus Jakarta Sans';
    font-size: 14px !important;
    font-style: normal;
    font-weight: 400 !important;
    line-height: 20px;
    transition: all 0.2s;

    &:first-of-type {
      left: 0;
      right: auto;
    }

    &:last-of-type {
      right: 0;
      left: auto;
    }

    span.text {
      display: inline;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      border-color: #c4c4c4 !important;
    }

    &:hover:not(:disabled) {
      background: #f9fafb;
    }
  }
`;

type PageItem = number | '...';

interface ReviewPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const ReviewPagination = ({ currentPage, totalPages, onPageChange }: ReviewPaginationProps) => {
  const isPC = useMediaQuery({ minWidth: 768 });
  const Icon = isPC ? ChevronIcon : ArrowIcon;

  if (totalPages <= 1) {
    return null;
  }

  const renderPageNumbers = (): PageItem[] => {
    const pages: PageItem[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push('...');
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <Container>
      <ArrowButton
        style={{
          left: 0,
          right: 'auto',
          position: window.innerWidth >= 768 ? 'absolute' : 'static',
        }}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <Icon direction="left" disabled={currentPage === 1} />
        <span className="text">Previous</span>
      </ArrowButton>

      <PaginationNumberWrapper>
        {renderPageNumbers().map((page, index) => (
          <PaginationButton
            key={index}
            isActive={page === currentPage}
            onClick={() => typeof page === 'number' && onPageChange(page)}
            disabled={typeof page !== 'number'}
          >
            {page}
          </PaginationButton>
        ))}
      </PaginationNumberWrapper>

      <ArrowButton
        style={{
          right: 0,
          left: 'auto',
          position: window.innerWidth >= 768 ? 'absolute' : 'static',
        }}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <span className="text">Next</span>
        <Icon direction="right" disabled={currentPage === totalPages} />
      </ArrowButton>
    </Container>
  );
};

export default ReviewPagination;
